import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@clerk/clerk-react";
import { queryKeys } from "@/lib/api/queryKeys";
import { toast } from "@/lib/toast";
import { useCreateNotification } from "@/features/notifications/hooks/useCreateNotification";
import type { JobStage, UploadConsultaResult } from "../types";
import {
  analyzeTranscript,
  type AnalyzeTranscriptOptions,
} from "../api/analyzeTranscript";

export interface UseAnalyzeTranscriptResult {
  stage: JobStage;
  mutate: (
    opts: Omit<AnalyzeTranscriptOptions, "signal" | "token">,
  ) => void;
  abort: () => void;
  isPending: boolean;
  error: Error | null;
  data: UploadConsultaResult | undefined;
  reset: () => void;
}

export function useAnalyzeTranscript(): UseAnalyzeTranscriptResult {
  const [stage, setStage] = useState<JobStage>("idle");
  const abortRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();
  const { session } = useSession();
  const createNotification = useCreateNotification();

  const mutation = useMutation({
    mutationFn: async (
      opts: Omit<AnalyzeTranscriptOptions, "signal" | "token">,
    ) => {
      const token = await session?.getToken();
      if (!token) throw new Error("Usuário não autenticado.");
      const controller = new AbortController();
      abortRef.current = controller;
      setStage("analyzing");
      return analyzeTranscript({
        ...opts,
        token,
        signal: controller.signal,
      });
    },
    onSuccess: (data) => {
      setStage("done");
      void queryClient.invalidateQueries({ queryKey: queryKeys.consultas.all });
      const href = `/consultas/${data.consultaId}`;
      toast.success("Análise de consulta pronta", {
        action: { label: "Abrir", onClick: () => { window.location.assign(href); } },
      });
      void createNotification({
        type: "decodificacao_done",
        title: "Análise de consulta pronta",
        href,
        metadata: { jobId: data.jobId, consultaId: data.consultaId },
      });
    },
    onError: () => {
      setStage("error");
    },
  });

  const abort = () => {
    abortRef.current?.abort();
    setStage("idle");
    mutation.reset();
  };

  const reset = () => {
    setStage("idle");
    mutation.reset();
  };

  return {
    stage,
    mutate: mutation.mutate,
    abort,
    isPending: mutation.isPending,
    error: mutation.error,
    data: mutation.data,
    reset,
  };
}
